import { mutationOptions, queryOptions } from "@tanstack/react-query";
import { z } from "zod";
import type { Answer, AnswerRequest, Form } from "../dynamicForm";
import { CriticalitySchema, ScoreSchema } from "../classification/schemas";
import type { Score } from "../classification/schemas";
import type { CustomHistoryEntry, HistoryEntry } from "../dynamicHistory/types";
import { api } from "./apiClient";
import { FORMS_API_BASE_ENDPOINT } from "./constants";
import { invalidateAll } from "./invalidation";

const BASE_ENDPOINT = `${FORMS_API_BASE_ENDPOINT}/information-types`;

const keys = {
  all: () => ["informationTypes"] as const,
  byId: (id: string) => [...keys.all(), id] as const,
  gridView: () => [...keys.all(), "gridview"] as const,
  form: () => [...keys.all(), "form"] as const,
  history: (id: string) => [...keys.all(), id, "history"] as const,
  selectOptions: () => [...keys.all(), "select-options"] as const,
} as const;

export type ConnectionType = "stored" | "used";

const InformationTypeGridViewRowSchema = z.object({
  id: z.string(),
  name: z.string(),
  archivedAt: z.string().optional(),
  answers: z.custom<Array<Answer>>(),
  classification: z
    .object({
      criticality: CriticalitySchema.nullish(),
      scores: z.array(ScoreSchema),
    })
    .nullable(),
});

const GetInformationTypesGridViewResponseSchema = z.object({
  informationTypes: z.array(InformationTypeGridViewRowSchema),
});

export const informationTypesGridViewQueryOptions = () =>
  queryOptions({
    queryKey: keys.gridView(),
    queryFn: () => api.get(`${BASE_ENDPOINT}/gridview`, { schema: GetInformationTypesGridViewResponseSchema }),
    throwOnError: true,
  });

const InformationTypeFormSchema = z.custom<Form>();

export const informationTypeFormQueryOptions = () =>
  queryOptions({
    queryKey: keys.form(),
    queryFn: () => api.get(`${BASE_ENDPOINT}/form`, { schema: InformationTypeFormSchema }),
    throwOnError: true,
  });

const ConnectedSystemSchema = z.object({
  connectionId: z.string(),
  systemId: z.string(),
  name: z.string(),
  createdAt: z.string(),
  classification: z.object({ criticality: CriticalitySchema.nullish() }).nullish(),
});
export type ConnectedSystem = z.infer<typeof ConnectedSystemSchema>;

const ConnectedSystemsInfoSchema = z.object({
  storedConnections: z.array(ConnectedSystemSchema),
  usedConnections: z.array(ConnectedSystemSchema),
});

const ClassificationSchema = z.object({
  scores: z.array(ScoreSchema),
  criticality: CriticalitySchema.nullish(),
  rationale: z.string().optional(),
});

const InformationTypeByIdSchema = z.object({
  id: z.string(),
  name: z.string(),
  archivedAt: z.string().optional(),
  answers: z.custom<Array<Answer>>(),
  connectedSystemsInfo: ConnectedSystemsInfoSchema,
  classification: ClassificationSchema.nullable(),
});

export type InformationTypeById = z.infer<typeof InformationTypeByIdSchema>;

export const informationTypeByIdQueryOptions = (id: string) =>
  queryOptions({
    queryKey: keys.byId(id),
    queryFn: () => api.get(`${BASE_ENDPOINT}/${id}`, { schema: InformationTypeByIdSchema }),
    throwOnError: true,
  });

export const updateInformationTypeClassificationMutationOptions = (informationTypeId: string) =>
  mutationOptions({
    mutationFn: ({ scores, rationale }: { scores: Array<Score>; rationale: string }) =>
      api.put(`${BASE_ENDPOINT}/${informationTypeId}/classification`, { scores, rationale }),
    onSuccess: () => invalidateAll(),
  });

const CreateInformationTypeResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
});

export type CreateInformationTypePayload = { name: string };

export const createInformationTypeMutationOptions = () =>
  mutationOptions({
    mutationFn: async (payload: CreateInformationTypePayload) =>
      await api.post(BASE_ENDPOINT, payload, { schema: CreateInformationTypeResponseSchema }),
    onSuccess: () => invalidateAll(),
  });

const UpdateInformationTypePayloadSchema = z.object({
  name: z.string().min(1),
});

export type UpdateInformationTypePayload = z.infer<typeof UpdateInformationTypePayloadSchema>;

export const updateInformationTypeMutationOptions = () =>
  mutationOptions({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateInformationTypePayload }) =>
      api.patch(`${BASE_ENDPOINT}/${id}`, payload, { schema: UpdateInformationTypePayloadSchema }),
    onSuccess: () => invalidateAll(),
  });

export const updateInformationTypeAnswersMutationOptions = () =>
  mutationOptions({
    mutationFn: ({ informationTypeId, answers }: { informationTypeId: string; answers: Array<AnswerRequest> }) =>
      api.patch(`${BASE_ENDPOINT}/${informationTypeId}/answers`, answers),
    onSuccess: () => invalidateAll(),
  });

export const deleteInformationTypeMutationOptions = () =>
  mutationOptions({
    mutationFn: (id: string) => api.delete(`${BASE_ENDPOINT}/${id}`),
    onSuccess: (_, id) => invalidateAll({ ignore: [keys.byId(id)] }),
  });

export const archiveInformationTypeMutationOptions = (id: string) =>
  mutationOptions({
    mutationFn: () => api.patch(`${BASE_ENDPOINT}/${id}/archive`),
    onSuccess: () => invalidateAll(),
  });

export const unarchiveInformationTypeMutationOptions = (id: string) =>
  mutationOptions({
    mutationFn: () => api.patch(`${BASE_ENDPOINT}/${id}/unarchive`),
    onSuccess: () => invalidateAll(),
  });

const InformationTypeSelectOptionsResponseSchema = z.array(z.object({ id: z.string(), name: z.string() }));

export const informationTypeSelectOptionsQueryOptions = () =>
  queryOptions({
    queryKey: keys.selectOptions(),
    queryFn: () => api.get(`${BASE_ENDPOINT}/select-options`, { schema: InformationTypeSelectOptionsResponseSchema }),
    throwOnError: true,
  });

type CreateSystemConnectionPayload = {
  informationTypeId: string;
  systemId: string;
  systemName: string;
  connectionType: ConnectionType;
};

export const createInformationTypeSystemConnectionMutationOptions = () =>
  mutationOptions({
    mutationFn: ({ informationTypeId, systemId, connectionType }: CreateSystemConnectionPayload) =>
      api.post(`${BASE_ENDPOINT}/${informationTypeId}/systems/${systemId}`, { connectionType }),
    onSuccess: () => invalidateAll(),
  });

type DeleteSystemConnectionPayload = { informationTypeId: string; connectionId: string };
export const deleteInformationTypeSystemConnectionMutationOptions = () =>
  mutationOptions({
    mutationFn: ({ informationTypeId, connectionId }: DeleteSystemConnectionPayload) =>
      api.delete(`${BASE_ENDPOINT}/${informationTypeId}/systems/${connectionId}`),
    onSuccess: () => invalidateAll(),
  });

type InformationTypeHistoryCustomEntry =
  | CustomHistoryEntry<"informationTypeName", string>
  | CustomHistoryEntry<
      "informationTypeClassificationScores",
      { templateKey: string; scores: Array<Score>; rationale?: string | null } | null
    >
  | CustomHistoryEntry<
      "informationTypeConnectedSystem",
      {
        connectionType: ConnectionType;
        connections: Array<{ systemId: string; systemName: string }>;
      } | null
    >
  | CustomHistoryEntry<"archived", boolean>;

type GetInformationTypeHistoryResponse = {
  history: Array<HistoryEntry<InformationTypeHistoryCustomEntry>>;
};

export const informationTypeHistoryQueryOptions = (id: string) =>
  queryOptions({
    queryKey: keys.history(id),
    queryFn: () => api.get(`${BASE_ENDPOINT}/${id}/history`) as Promise<GetInformationTypeHistoryResponse>,
    throwOnError: true,
  });

export const createInformationTypeUpsellContactRequestMutationOptions = () =>
  mutationOptions({
    mutationFn: () => api.post(`${BASE_ENDPOINT}/upsell-contact-request`),
  });
